import { createFileRoute } from "@tanstack/react-router";
import portrait from "@/assets/portrait.jpg";
import bookCover1 from "@/assets/book-cover-ai.jpg";
import bookCover2 from "@/assets/book-cover-climate.jpg";
import bookCover3 from "@/assets/book-cover-loneliness.jpg";
import { hreflangLinks } from "@/lib/seo";
import { personSchema, breadcrumbSchema, EN_HOME } from "@/lib/en-schema";
import { BOOK_TITLES, DISCLOSURE_EN } from "@/content/books";



const CANONICAL = "https://smartsustainability.ai/about";
const SITE = "https://akillisurdurulebilirlik.com";
const TITLE = "About the author — Arda Öztaşkın";
const DESCRIPTION =
  "Arda Öztaşkın is a Turkish writer and columnist on AI, climate, and institutional trust. Biography, areas of expertise, and books.";

const expertise = [
  "Artificial intelligence and society",
  "Climate crisis and sustainability",
  "Corporate communications and brand strategy",
  "Sustainability communications",
  "Technology ethics and the future",
  "Humans, loneliness, and the digital age",
];

const books = [
  {
    cover: bookCover3,
    title: BOOK_TITLES.loneliness.titleEn,
    original: "Yalnızlık A.Ş.",
    year: "2026",
    publisher: "Independent",
    summary:
      "How loneliness became a market — and what a society organised around it forgets about being human.",
    amazon: "https://www.amazon.com/dp/B0H59HPG62",
  },
  {
    cover: bookCover1,
    title: BOOK_TITLES.ai.titleEn,
    original: "Yapay Zekâ: Gücün İki Yüzü",
    year: "2024",
    publisher: "Destek Yayınları",
    summary:
      "A field guide to AI along the axis of promise and risk — practical framing for individuals and institutions.",
    amazon: "https://www.amazon.com/dp/B0H58HHS3B",
  },
  {
    cover: bookCover2,
    title: BOOK_TITLES.climate.titleEn,
    original: "İklim Değil, İnsanlık Krizi",
    year: "2023",
    publisher: "Yeni İnsan Yayınevi",
    summary:
      "A reading of the climate crisis as a crisis of the human — and a case for personal responsibility inside systemic change.",
    amazon: "https://www.amazon.com/dp/B0H598BYTV",
  },
];

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "profile" },
      { property: "og:url", content: CANONICAL },
      { property: "og:locale", content: "en_US" },
      { property: "og:locale:alternate", content: "tr_TR" },
      { property: "og:image", content: `${SITE}/og-image.jpg` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
      ...hreflangLinks("/yazar", "/about"),
    ],

    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfilePage",
          url: CANONICAL,
          inLanguage: "en",
          mainEntity: personSchema(),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbSchema([
            { name: "Home", item: EN_HOME },
            { name: "About", item: CANONICAL },
          ]),
        ),
      },
    ],

  }),
  component: EnAuthorPage,
});

const serif = "font-['Fraunces',Georgia,serif]";

function EnAuthorPage() {
  return (
    <article className="mx-auto max-w-5xl px-6 py-16 md:py-20 space-y-16 md:space-y-20">
      {/* Hero */}
      <header className="grid md:grid-cols-[1fr_2fr] gap-10 items-center">
        <img
          src={portrait}
          alt="Arda Öztaşkın portrait"
          width={512}
          height={512}
          loading="eager"
          className="w-48 md:w-full aspect-square rounded-full object-cover grayscale"
        />
        <div className="space-y-5">
          <span className="text-clay text-[10px] uppercase tracking-[0.28em] font-semibold">
            About the author
          </span>
          <h1 className={`${serif} text-4xl md:text-6xl leading-[1.05] tracking-tight text-ink text-balance`}>
            Arda <em className="italic">Öztaşkın</em>
          </h1>
          <p className={`${serif} text-lg md:text-xl text-ink/80 leading-relaxed`}>
            Turkish writer and columnist on AI, climate, and institutional
            trust — and the vocabulary that collision needs.
          </p>
          <div className="flex flex-wrap gap-3 pt-2 text-[10px] uppercase tracking-[0.2em] font-semibold">
            <a
              href="mailto:ardaoztaskin@gmail.com"
              className="border border-ink/30 hover:border-clay hover:text-clay px-4 py-2 rounded-full transition-colors"
            >
              Contact
            </a>
            <a
              href="https://www.linkedin.com/in/ardaoztaskin/"
              target="_blank"
              rel="noopener noreferrer me"
              className="border border-ink/30 hover:border-clay hover:text-clay px-4 py-2 rounded-full transition-colors"
            >
              LinkedIn
            </a>
          </div>
        </div>
      </header>

      {/* Biography */}
      <section className="border-t border-border pt-12 md:pt-16">
        <span className="text-clay text-[10px] uppercase tracking-[0.28em] font-semibold">01 · Biography</span>
        <h2 className={`${serif} text-3xl md:text-4xl mt-3 mb-6 leading-tight tracking-tight text-ink`}>
          A writer at the intersection of AI, climate, and institutions
        </h2>
        <div className={`${serif} space-y-5 text-ink/85 leading-relaxed text-lg max-w-3xl`}>
          <p>
            Arda Öztaşkın is a writer and communications executive working at
            the intersection of artificial intelligence, sustainability, and
            systems thinking. He has led corporate communications, brand
            strategy, and social impact functions for more than two decades at
            leading Turkish institutions.
          </p>
          <p>
            His column appears weekly in Ekonomi Gazetesi; his essays and
            contributions are published in Harvard Business Review Türkiye and
            on Smart Sustainability, the English section of his platform. He
            writes about the collision of AI, climate, and institutional
            trust — and coins the vocabulary that collision needs.
          </p>
          <p>
            He is the author of three books:{" "}
            <em>{BOOK_TITLES.loneliness.titleEn}</em> ({BOOK_TITLES.loneliness.year}),{" "}
            <em>{BOOK_TITLES.ai.titleEn}</em> ({BOOK_TITLES.ai.year}), and{" "}
            <em>{BOOK_TITLES.climate.titleEn}</em> ({BOOK_TITLES.climate.year}).
          </p>
          <p className="text-sm text-ink/55 not-italic font-sans leading-relaxed">
            {DISCLOSURE_EN}
          </p>
        </div>
      </section>

      {/* Expertise */}
      <section className="border-t border-border pt-12 md:pt-16">
        <span className="text-clay text-[10px] uppercase tracking-[0.28em] font-semibold">02 · Expertise</span>
        <h2 className={`${serif} text-3xl md:text-4xl mt-3 mb-6 leading-tight tracking-tight text-ink`}>
          Areas of work
        </h2>
        <ul className="grid sm:grid-cols-2 gap-3">
          {expertise.map((e) => (
            <li
              key={e}
              className="border border-border rounded-lg px-4 py-3 text-ink/85 hover:border-clay/50 transition-colors"
            >
              {e}
            </li>
          ))}
        </ul>
      </section>

      {/* Books */}
      <section className="border-t border-border pt-12 md:pt-16">
        <span className="text-clay text-[10px] uppercase tracking-[0.28em] font-semibold">03 · Books</span>
        <h2 className={`${serif} text-3xl md:text-4xl mt-3 mb-8 leading-tight tracking-tight text-ink`}>
          Three books
        </h2>
        <div className="grid sm:grid-cols-3 gap-8">
          {books.map((b) => (
            <a
              key={b.title}
              href={b.amazon}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col gap-3"
            >
              <div className="overflow-hidden bg-ink/5 aspect-[2/3]">
                <img
                  src={b.cover}
                  alt={`${b.title} book cover`}
                  width={300}
                  height={450}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover shadow-xl transition-transform duration-500 group-hover:scale-[1.03]"
                />
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-ink/60">
                {b.year} · {b.publisher}
              </div>
              <h3 className={`${serif} text-xl leading-snug text-ink group-hover:text-clay transition-colors`}>
                {b.title}
              </h3>
              <p className="text-[13px] text-ink/60 italic -mt-1">
                Original title: {b.original}
              </p>
              <p className="text-[14px] leading-relaxed text-ink/75">
                {b.summary}
              </p>
              <span className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] font-bold text-clay border-b border-clay/40 group-hover:border-clay pb-0.5 self-start">
                Find on Amazon →
              </span>
            </a>
          ))}
        </div>
      </section>
    </article>
  );
}
